import { useEffect, useMemo, useState } from 'react';
import { Radio, Select } from 'tdesign-react';

import { Empty2, IPBot } from '@/components/icon';
import { Voice, VoiceAge, VoiceGender, VoiceType } from '@/interface/aigc';
import { fetchVoiceList } from '@/services/bot';
import { isPic } from '@/utils/getApiDomain';

import { audioManager } from './audioManager';
import VoiceCard from './card';
import { AGE_OPTIONS, GENDER_OPTIONS } from './config';
import styles from './index.module.less';

interface IProps {
  selectedVoice?: Voice;
  getSelected: (src?: Voice) => void;
}

const VoiceList: React.FC<IProps> = (props) => {
  const { selectedVoice, getSelected } = props;
  const [genderFilter, setGenderFilter] = useState<VoiceGender>();
  const [ageFilter, setAgeFilter] = useState<VoiceAge>();
  const [showIP, setShowIP] = useState(false);
  const [fullList, setFullList] = useState<Voice[]>([]);
  const [playingId, setPlayingId] = useState<string>('');

  useEffect(() => {
    const initList = async () => {
      const voiceList = await fetchVoiceList();
      setFullList(voiceList);
    };

    initList();

    // 设置播放完成回调
    audioManager.setOnEnded(() => {
      setPlayingId('');
    });

    // 组件卸载时停止播放
    return () => {
      audioManager.destroy();
    };
  }, []);

  const list = useMemo(() => fullList.filter((voice) => {
    // 性别筛选
    if (genderFilter !== undefined && voice.gender !== genderFilter) {
      return false;
    }

    // 年龄筛选
    if (ageFilter !== undefined && voice.ageGroup !== ageFilter) {
      return false;
    }

    // IP筛选
    if (showIP && voice.voiceType !== VoiceType.CAI) {
      return false;
    }

    // 绘本筛选
    if (isPic && (voice.voiceType === VoiceType.CAI || voice.voiceType === VoiceType.HUNYUAN_IPBOT)) {
      return false;
    }

    return true;
  }), [fullList, genderFilter, ageFilter, showIP, isPic]);

  const handleClick = (val: Voice) => {
    if (val.voiceId !== selectedVoice?.voiceId) {
      getSelected(val);
    } else {
      getSelected();
    }
  };

  const handlePlayAudio = async (voice: Voice) => {
    if (!voice.url || !voice.voiceId) return;

    const isPlaying = await audioManager.toggle(voice.url);
    setPlayingId(isPlaying ? voice.voiceId : '');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.left}>
          <Select
            value={genderFilter}
            label="性别"
            clearable={true}
            options={GENDER_OPTIONS}
            onChange={v => setGenderFilter(v === null ? undefined : v as VoiceGender)}
            style={{ width: 150 }}
          />
          <Select
            value={ageFilter}
            label="年龄"
            clearable={true}
            options={AGE_OPTIONS}
            onChange={v => setAgeFilter(v === null ? undefined : v as VoiceAge)}
            style={{ width: 150 }}
          />
          {!isPic && <Radio.Group
            value={showIP}
            allowUncheck={true}
            onChange={() => setShowIP(!showIP)}
          >
            <Radio.Button value={true}>
              <div className={styles.ip}>
                <IPBot />
                IP角色声音
              </div>
            </Radio.Button>
          </Radio.Group>}
        </div>
      </div>
      {list.length === 0 ? (
        <div className={styles.placeholder}>
          <Empty2 />
          <div>暂无音色</div>
        </div>
      ) : (
        <div className={styles.list}>
          {list.map((e, i) => (
            <div
              key={e.voiceId || e.voiceName || i}
              className={styles.card}
              onClick={() => handleClick(e)}
            >
              <VoiceCard
                data={e}
                selectedVoice={selectedVoice}
                isPlaying={playingId === e.voiceId}
                onPlayAudio={handlePlayAudio}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceList;
